import { AppInputSwitcher } from "../../components/Layouts/AppInputSwitcher/AppInputSwitcher";
import React, { useEffect, useRef, useState } from "react";
import { createFilterOptions } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BOOKING_ACTIONS } from "../../store/onlineBooking";
import { useDispatch } from "react-redux";
import BookingGetters from "../../store/onlineBooking/getters";
import { useWebCheckInForm } from "../../hooks/WebCheckInForm_2";
import { PrivateCustomerDetailFormField } from "./UserDetailsForm";
import {
  DefaultValuesDetailsForm,
  DetailsFormValues,
} from "../../components/Layouts/WebCheckInForms/WebCheckInForms.props";
import { useSearchParams } from "react-router-dom";
import AppCountrySelect from "../../components/Layouts/AppCountrySelect";

function PrivatePersonalDetails({
  userDetails,
  setUserDetails,
  dateError,
  isAirport,
  hideNumberOfFieldsAtPayAtDesk,
  hideNumberOfFieldsAtPayNow,
  paymentType,
  privateValidateRef,
  isBillingOptional = false,
  customerDetails,
}: any) {
  const [searchParams] = useSearchParams();
  const isResSoucePayNow =
    searchParams.get("reservationSourcePayNow") === "true";
  const isScriptChannel =
    searchParams.get("isInvalidScriptChannelResSource") === "true";
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const appSettings = BookingGetters.getAppSettings();
  const enableUppercaseInputField = appSettings?.enableUppercaseInputField;

  const [formStates, formFields] = useWebCheckInForm(
    { ...PrivateCustomerDetailFormField },
    userDetails,
    (e) => {
      setUserDetails((prevDetails) => {
        return {
          ...prevDetails,
          customerType: "Private",
          ...e,
        };
      });
    }
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!privateValidateRef) return;

    const validate = async (): Promise<boolean> => {
      if (isBillingOptional) return true;

      const shouldValidateFull =
        appSettings?.showOnePayMethodTwoPrice ||
        !(
          (paymentType === "payNow" && hideNumberOfFieldsAtPayNow) ||
          (paymentType === "payLater" && hideNumberOfFieldsAtPayAtDesk)
        );

      const alwaysRequired = ["sex", "ssnNumber", "birthNation"];
      const conditionalRequired = [
        "country",
        "city",
        "state",
        "zip",
        "streetName",
        "streetNumber",
        "birthPlace",
        "birthProvince",
        "dateOfBirth",
      ];
      const keysToCheck = shouldValidateFull
        ? [...alwaysRequired, ...conditionalRequired]
        : alwaysRequired;

      const keyToLabel: Record<string, string> = Object.values(
        formFields
      ).reduce((acc, field: any) => {
        if (field.key && field.label) {
          acc[field.key] = field.label;
        }
        return acc;
      }, {} as Record<string, string>);

      const newErrors: Record<string, string> = {};
      keysToCheck.forEach((key) => {
        if (!userDetails[key]) {
          const label = keyToLabel[key] || key;
          newErrors[key] = `${translate(label)} ${translate("is required")}`;
        }
      });

      shouldAutoFocusRef.current = true;
      setFieldErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    privateValidateRef.current = validate;
  }, [
    privateValidateRef,
    userDetails,
    paymentType,
    hideNumberOfFieldsAtPayNow,
    hideNumberOfFieldsAtPayAtDesk,
    appSettings,
    translate,
    isBillingOptional,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(BOOKING_ACTIONS.setUserDetails(userDetails));
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [userDetails, dispatch]);

  useEffect(() => {
    setFieldErrors((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (userDetails[key]) {
          updated[key] = "";
        }
      });
      return updated;
    });
  }, [userDetails]);

  const isSmallScreen = window.innerWidth <= 768;
  const istabletScreen = window.innerWidth > 768 && window.innerWidth < 1300;
  const isGretaBookingNewUi = appSettings?.isGretaBookingNewUi;

  const formContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoFocusRef = useRef(false);

  // Focus the first field that has a validation error (Greta UI only)
  useEffect(() => {
    if (!isGretaBookingNewUi) return;
    if (!shouldAutoFocusRef.current) return;
    shouldAutoFocusRef.current = false;

    if (Object.values(fieldErrors).every((v) => !v)) return;

    const firstErrorKey = Object.values(formFields)
      .map((f: any) => f.key)
      .find((key) => !!fieldErrors[key]);

    if (!firstErrorKey || !formContainerRef.current) return;

    const input = formContainerRef.current.querySelector<HTMLInputElement>(
      `[data-field-key="${firstErrorKey}"] input`
    );
    input?.focus();
  }, [fieldErrors]);

  useEffect(() => {
    if (!userDetails?.sex) return;

    let normalized = userDetails.sex;

    if (["MASCULINO", "HOMME"].includes(userDetails.sex)) {
      normalized = "MASCHIO";
    }

    if (["FEMININA", "FEMME"].includes(userDetails.sex)) {
      normalized = "FEMMINA";
    }

    setUserDetails((prev: any) => ({ ...prev, sex: normalized }));
  }, [userDetails?.sex]);

  const cityFilterOptions = createFilterOptions({
    limit: 50,
    matchFrom: "start",
  });

  const mapSexForUI = (sex: string): string => {
    if (sex === "MASCHIO") return translate("MALE");
    if (sex === "FEMMINA") return translate("FEMALE");
    return sex;
  };

  useEffect(() => {
    if (!customerDetails) return;

    const formattedData = {
      ...DefaultValuesDetailsForm,
      ...customerDetails,
      sex:
        customerDetails.sex === "MALE"
          ? "MASCHIO"
          : customerDetails.sex === "FEMALE"
          ? "FEMMINA"
          : customerDetails.sex || "",
    };

    setUserDetails(formattedData);
    formStates.reset({
      ...formattedData,
    });

    setTimeout(() => {
      formStates.setValue("birthPlace", formattedData.birthPlace || "");
      formStates.setValue("birthProvince", formattedData.birthProvince || "");
      formStates.setValue("ssnNumber", formattedData.ssnNumber || "");
    }, 100);
  }, [customerDetails]);
  return (
    <>
      <div
        className={
          isGretaBookingNewUi
            ? "bg-gray-100 box-border pt-4 pb-4 pr-4 w-full md:w-[85%]"
            : "bg-white box-border justify-center"
        }
        style={
          isGretaBookingNewUi
            ? undefined
            : { padding: isSmallScreen ? "0px" : "2rem" }
        }
      >
        <div
          ref={formContainerRef}
          className={
            isGretaBookingNewUi
              ? "flex flex-col w-full gap-3"
              : `grid sm:grid-cols-2 w-full ${
                  istabletScreen ? "md:grid-cols-2" : "md:grid-cols-3"
                } gap-4`
          }
        >
          {Object.values(formFields).map((el) => {
            if (el.key === "flightNumber" && !appSettings?.flightNumber) {
              return null;
            }
            let isRequired;
            if (isBillingOptional) {
              isRequired = false;
            } else if (el.key === "flightNumber") {
              if (appSettings?.flightNumberRequired) {
                isRequired = true;
              } else if (
                appSettings?.flightNumberRequiredForAirport &&
                isAirport === "true"
              ) {
                isRequired = true;
              } else {
                isRequired = false;
              }
            } else {
              isRequired = true;
            }
            if (
              !isBillingOptional &&
              (el.key === "country" ||
                el.key === "state" ||
                el.key === "zip" ||
                el.key === "streetName" ||
                el.key === "birthNation" ||
                el.key === "dateOfBirth" ||
                el.key === "birthPlace" ||
                el.key === "birthProvince" ||
                el.key === "ssnNumber" ||
                el.key === "sex" ||
                el.key === "city" ||
                el.key === "streetNumber")
            ) {
              if (appSettings?.showOnePayMethodTwoPrice) {
                isRequired = true;
              } else {
                isRequired = !(
                  (paymentType === "payNow" && hideNumberOfFieldsAtPayNow) ||
                  (paymentType === "payLater" && hideNumberOfFieldsAtPayAtDesk)
                );
              }
            }

            if (el.key === "country" || el.key === "birthNation") {
              return (
                <div key={el.key} data-field-key={el.key}>
                  <div
                    className={
                      isGretaBookingNewUi
                        ? "relative z-20 w-full box-border bg-white"
                        : "country-section"
                    }
                  >
                    <AppCountrySelect
                      label={el.label}
                      fieldKey={el.key}
                      value={userDetails[el.key]}
                      options={el.options || []}
                      required={isRequired}
                      translate={translate}
                      formStates={formStates}
                      setUserDetails={setUserDetails}
                      onChange={(val: string) => {
                        formStates.setValue(el.key, val);

                        if (isGretaBookingNewUi) {
                          if (!val) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              [el.key]: "This field is mandatory",
                            }));
                          } else {
                            setFieldErrors((prev) => ({
                              ...prev,
                              [el.key]: "",
                            }));
                          }
                        }
                      }}
                      cityFieldName={
                        el.key === "country" ? "city" : "birthPlace"
                      }
                      stateFieldName={
                        el.key === "country" ? "state" : "birthProvince"
                      }
                      enableUppercaseInputField={enableUppercaseInputField}
                    />
                  </div>
                  {isGretaBookingNewUi && fieldErrors[el.key] && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors[el.key]}
                    </p>
                  )}
                </div>
              );
            }
            return (
              <div
                key={el.key}
                data-field-key={el.key}
                className={isGretaBookingNewUi ? "relative z-0" : ""}
              >
                <AppInputSwitcher
                  {...el}
                  required={isRequired}
                  placeholder={translate(el.placeholder || "")}
                  label={translate(el.label || "")}
                  error={!!fieldErrors[el.key]}
                  className={
                    isGretaBookingNewUi
                      ? el.key === "sex"
                        ? "w-full box-border mt-4 mb-4"
                        : "w-full box-border bg-white"
                      : "h-20 sm:w-full box-border sm:px-0 md:px-8"
                  }
                  value={
                    el.key === "sex"
                      ? // ? mapSexForUI(userDetails.sex)
                        // : userDetails[el.key] || formStates.getValues(el.key)
                        mapSexForUI(
                          formStates.getValues(el.key) ||
                            userDetails?.[el.key] ||
                            ""
                        )
                      : formStates.getValues(el.key) ??
                        userDetails?.[el.key] ??
                        ""
                  }
                  options={
                    el.key === "sex"
                      ? [translate("MALE"), translate("FEMALE")]
                      : el.options
                  }
                  filterOptions={
                    el?.key === "city" || el?.key === "birthPlace"
                      ? cityFilterOptions
                      : undefined
                  }
                  dateError={dateError}
                  enableUppercaseInputField={enableUppercaseInputField}
                  onchange={(e: any) => {
                    const val =
                      enableUppercaseInputField && typeof e === "string"
                        ? e.toUpperCase()
                        : e?.toString?.() || "";
                    // : e.toString();

                    // const val = e?.toString() || "";

                    formStates.setValue(el.key, val, {});
                    setUserDetails((prev: any) => ({
                      ...prev,
                      [el.key]: val,
                    }));

                    if (!isGretaBookingNewUi) return;

                    setFieldErrors((prev) => ({
                      ...prev,
                      [el.key]: val ? "" : "This field is mandatory",
                    }));
                  }}
                />
                {isGretaBookingNewUi && fieldErrors[el.key] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors[el.key]}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default PrivatePersonalDetails;
